import { useState, useMemo } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  TextField,
} from "@mui/material";
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  Check as CheckIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import { ICoreProductCategory } from "../../types/core";
import useDeleteCoreProductCategory from "../../services/useDeleteCoreProductCategory";
import useUpdateCoreProductCategory from "../../services/useUpdateCoreProductCategory";

interface CategoryListProps {
  categories: ICoreProductCategory[];
  onError?: (message: string) => void;
}

/**
 * Lista de categorias de produto com opção de exclusão e edição de ordem
 */
const CategoryList = ({ categories, onError }: CategoryListProps) => {
  const [categoryToDelete, setCategoryToDelete] =
    useState<ICoreProductCategory | null>(null);
  const [editingCategoryId, setEditingCategoryId] = useState<number | null>(
    null,
  );
  const [editingOrder, setEditingOrder] = useState<string>("");

  const deleteCategoryMutation = useDeleteCoreProductCategory();
  const updateCategoryMutation = useUpdateCoreProductCategory();

  // Ordenar categorias por ordem primeiro, depois alfabeticamente
  const sortedCategories = useMemo(() => {
    return [...categories].sort((a, b) => {
      if (a.order !== b.order) {
        return a.order - b.order;
      }
      return a.name.localeCompare(b.name, "pt-BR", { sensitivity: "base" });
    });
  }, [categories]);

  const handleDeleteClick = (category: ICoreProductCategory) => {
    setCategoryToDelete(category);
  };

  const handleEditOrderClick = (category: ICoreProductCategory) => {
    setEditingCategoryId(category.id);
    setEditingOrder(String(category.order));
  };

  const handleCancelEdit = () => {
    setEditingCategoryId(null);
    setEditingOrder("");
  };

  const handleSaveOrder = (categoryId: number) => {
    const newOrder = parseInt(editingOrder, 10);
    if (isNaN(newOrder) || newOrder < 0) {
      onError?.("A ordem deve ser um número inteiro não negativo.");
      return;
    }

    updateCategoryMutation.mutate(
      { id: categoryId, order: newOrder },
      {
        onSuccess: () => {
          setEditingCategoryId(null);
          setEditingOrder("");
        },
        onError: (err: any) => {
          console.error("Erro ao atualizar ordem da categoria", err);
          const data = err?.response?.data ?? err;
          if (data && typeof data === "object") {
            const detail = data.detail;
            if (detail) {
              onError?.(detail);
            } else {
              const errorMessages = Object.keys(data).map((key) => {
                const value = (data as any)[key];
                const messages = Array.isArray(value) ? value : [value];
                return `${key}: ${messages.join(", ")}`;
              });
              onError?.(errorMessages.join("\n"));
            }
          } else {
            onError?.("Erro ao atualizar ordem da categoria. Tente novamente.");
          }
        },
      },
    );
  };

  const handleDeleteConfirm = () => {
    if (!categoryToDelete) return;

    deleteCategoryMutation.mutate(categoryToDelete.id, {
      onSuccess: () => {
        setCategoryToDelete(null);
      },
      onError: (err: any) => {
        console.error("Erro ao excluir categoria", err);
        const data = err?.response?.data ?? err;
        if (data && typeof data === "object") {
          const detail = data.detail;
          if (detail) {
            onError?.(detail);
          } else {
            const errorMessages = Object.keys(data).map((key) => {
              const value = (data as any)[key];
              const messages = Array.isArray(value) ? value : [value];
              return `${key}: ${messages.join(", ")}`;
            });
            onError?.(errorMessages.join("\n"));
          }
        } else {
          onError?.("Erro ao excluir categoria. Tente novamente.");
        }
        setCategoryToDelete(null);
      },
    });
  };

  const handleDeleteCancel = () => {
    setCategoryToDelete(null);
  };

  const canDelete = (category: ICoreProductCategory) => {
    return (category.products_count ?? 0) === 0;
  };

  return (
    <>
      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Categorias Existentes
          </Typography>
          {categories.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <Typography variant="body1" color="text.secondary">
                Nenhuma categoria encontrada.
              </Typography>
            </Box>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>Nome</TableCell>
                    <TableCell align="center">Ordem</TableCell>
                    <TableCell align="center">Produtos</TableCell>
                    <TableCell align="center">Ações</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedCategories.map((category) => (
                    <TableRow key={category.id} hover>
                      <TableCell>{category.id}</TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {category.name}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        {editingCategoryId === category.id ? (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              gap: 0.5,
                            }}
                          >
                            <TextField
                              type="number"
                              size="small"
                              value={editingOrder}
                              onChange={(e) => setEditingOrder(e.target.value)}
                              inputProps={{
                                min: 0,
                                style: { width: "60px", textAlign: "center" },
                              }}
                              autoFocus
                              onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                  handleSaveOrder(category.id);
                                } else if (e.key === "Escape") {
                                  handleCancelEdit();
                                }
                              }}
                            />
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleSaveOrder(category.id)}
                              disabled={updateCategoryMutation.isLoading}
                            >
                              <CheckIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              color="default"
                              onClick={handleCancelEdit}
                              disabled={updateCategoryMutation.isLoading}
                            >
                              <CloseIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        ) : (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              gap: 0.5,
                            }}
                          >
                            <span>{category.order}</span>
                            <Tooltip title="Editar ordem">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => handleEditOrderClick(category)}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {category.products_count ?? 0}
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip
                          title={
                            canDelete(category)
                              ? "Excluir categoria"
                              : "Não é possível excluir categorias com produtos"
                          }
                        >
                          <span>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDeleteClick(category)}
                              disabled={
                                !canDelete(category) ||
                                deleteCategoryMutation.isLoading
                              }
                            >
                              <DeleteIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Dialog de confirmação de exclusão */}
      <Dialog open={categoryToDelete !== null} onClose={handleDeleteCancel}>
        <DialogTitle>Confirmar Exclusão</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Tem certeza de que deseja excluir a categoria "
            {categoryToDelete?.name}"?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Cancelar</Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleteCategoryMutation.isLoading}
          >
            {deleteCategoryMutation.isLoading ? "Excluindo..." : "Excluir"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CategoryList;
